const OPEN_TIMEOUT_MS = 4000;
const FALLBACK_CONTROL_SELECTOR = 'input[type="email"]:not([disabled])';

export function openOmnisendForm(formId, target = globalThis) {
  const normalizedFormId = formId?.trim();
  if (!normalizedFormId) return false;

  const queue = target.omnisend || [];
  if (typeof queue.push !== 'function') return false;

  target.omnisend = queue;
  queue.push(['openForm', normalizedFormId]);
  return true;
}

export function isMatchingOmnisendView(event, formId) {
  const eventType = event?.detail?.type ?? event?.detail?.eventName;
  return eventType === 'view' && event.detail.form?.id === formId;
}

export function getOpenOmnisendForm(document, formId) {
  const normalizedFormId = formId?.trim();
  if (!normalizedFormId || !/^[a-zA-Z0-9_-]+$/.test(normalizedFormId)) return null;

  const container = document?.querySelector?.(`.omnisend-form-${normalizedFormId}-container`);
  if (!container || container.hidden || container.getAttribute?.('aria-hidden') === 'true') return null;

  return container;
}

const FooterSignupTriggerElement = globalThis.HTMLElement ?? class {};

export class FooterSignupTrigger extends FooterSignupTriggerElement {
  connectedCallback() {
    if (this.abortController) return;

    this.trigger = this.querySelector('[data-footer-signup-trigger]');
    this.fallback = this.querySelector('[data-footer-signup-fallback]');
    this.formId = this.dataset.omnisendFormId?.trim();
    this.view = this.ownerDocument?.defaultView ?? globalThis;
    this.document = this.ownerDocument;

    if (!this.trigger || !this.fallback || !this.formId) return;

    this.abortController = new AbortController();
    this.handleOmnisendEvent = this.handleOmnisendEvent.bind(this);
    this.trigger.addEventListener('click', () => this.openForm(), {
      signal: this.abortController.signal,
    });
    this.view.addEventListener('omnisendForms', this.handleOmnisendEvent, {
      signal: this.abortController.signal,
    });

    this.trigger.hidden = false;
    this.fallback.hidden = true;
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.popupObserver?.disconnect();
    this.clearOpenTimer();
    this.abortController = null;
  }

  openForm() {
    if (!this.trigger || !this.fallback || !this.formId || !this.view) return;

    this.clearOpenTimer();
    this.dataset.opening = 'true';
    this.trigger.setAttribute('aria-busy', 'true');
    this.watchForPopup();
    this.openTimer = this.view.setTimeout(() => this.showFallback(), OPEN_TIMEOUT_MS);

    if (!openOmnisendForm(this.formId, this.view)) {
      this.showFallback();
      return;
    }

    this.tryConfirmOpen();
  }

  handleOmnisendEvent(event) {
    if (this.dataset.opening !== 'true' || !isMatchingOmnisendView(event, this.formId)) return;

    this.confirmOpen();
  }

  watchForPopup() {
    this.popupObserver?.disconnect();
    if (!this.view.MutationObserver || !this.document?.documentElement) return;

    this.popupObserver = new this.view.MutationObserver(() => this.tryConfirmOpen());
    this.popupObserver.observe(this.document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'class', 'hidden'],
    });
  }

  tryConfirmOpen() {
    if (this.dataset.opening !== 'true' || !getOpenOmnisendForm(this.document, this.formId)) return false;

    this.confirmOpen();
    return true;
  }

  confirmOpen() {
    this.clearOpenTimer();
    this.popupObserver?.disconnect();
    delete this.dataset.opening;
    this.trigger.removeAttribute('aria-busy');
  }

  showFallback() {
    this.clearOpenTimer();
    this.popupObserver?.disconnect();
    delete this.dataset.opening;
    this.dataset.fallback = 'true';
    this.trigger.removeAttribute('aria-busy');
    this.trigger.hidden = true;
    this.fallback.hidden = false;

    const focusFallback = () => {
      this.fallback.querySelector(FALLBACK_CONTROL_SELECTOR)?.focus?.({ preventScroll: true });
    };

    if (typeof this.view.requestAnimationFrame === 'function') {
      this.view.requestAnimationFrame(focusFallback);
    } else {
      focusFallback();
    }
  }

  clearOpenTimer() {
    if (this.openTimer === undefined) return;

    this.view?.clearTimeout?.(this.openTimer);
    this.openTimer = undefined;
  }
}

if ('customElements' in globalThis && 'HTMLElement' in globalThis) {
  if (!customElements.get('footer-signup-trigger')) {
    customElements.define('footer-signup-trigger', FooterSignupTrigger);
  }
}
